import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, MapPin, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import ClientFormModal from '@/components/ClientFormModal';
import { propertyLabel } from '@/utils/createAppraisal';

const clientName = (c) => `${c.name_first || ''} ${c.name_last || ''}`.trim() || c.company_name || 'Client';

function ResultRow({ icon: Icon, title, sub, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors flex items-start gap-2.5">
      <Icon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800 truncate">{title}</span>
        {sub && <span className="block text-[11px] text-slate-400 truncate">{sub}</span>}
      </span>
    </button>
  );
}

// Search panel: mode 'go' sends you to the Client/Property page, mode 'free' to the Free Appraisal form.
export function FindClientOrProperty({ mode = 'go', onDone, onNewClient }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState({ clients: [], properties: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRes({ clients: [], properties: [] }); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/search', { params: { q: q.trim() } });
        setRes({ clients: data?.clients || [], properties: data?.properties || [] });
      } catch { setRes({ clients: [], properties: [] }); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);

  const go = (path) => { onDone?.(); navigate(path); };
  const pickClient = (c) => go(mode === 'free' ? `/free-appraisal?client_id=${c._id}` : `/clients/${c._id}`);
  const pickProperty = (p) => go(mode === 'free' ? `/free-appraisal?property_id=${p._id}` : `/properties/${p._id}`);
  const empty = q.trim().length >= 2 && !loading && !res.clients.length && !res.properties.length;

  return (
    <div data-testid={`find-client-property-${mode}`} className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} data-testid="find-client-property-input"
          placeholder="Search client name, email, phone or street…"
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#E2E2DF] rounded-lg focus:outline-none focus:border-[#D35400]" />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      <div className="max-h-[45vh] overflow-y-auto space-y-2" data-testid="find-client-property-results">
        {res.clients.length > 0 && <p className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Clients</p>}
        {res.clients.map(c => (
          <ResultRow key={c._id} icon={Users} title={clientName(c)} sub={c.email || c.phone_mobile || c.address_street || ''}
            onClick={() => pickClient(c)} testId={`find-result-client-${c._id}`} />
        ))}
        {res.properties.length > 0 && <p className="px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Properties</p>}
        {res.properties.map(p => (
          <ResultRow key={p._id} icon={MapPin} title={propertyLabel(p) || 'Property'} sub={[p.property_no && `P${p.property_no}`, p.client_name].filter(Boolean).join(' · ')}
            onClick={() => pickProperty(p)} testId={`find-result-property-${p._id}`} />
        ))}
        {empty && <p className="px-3 py-2 text-sm text-slate-400" data-testid="find-no-results">No matching client or property.</p>}
      </div>
      <div className="border-t border-slate-100 pt-3">
        {mode === 'free' ? (
          <button type="button" data-testid="find-new-client-property-btn" onClick={() => go('/free-appraisal?new=1')}
            className="flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900">
            <Plus size={14} /> New client + property (not on file yet)
          </button>
        ) : (
          <button type="button" data-testid="find-new-client-btn" onClick={onNewClient}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#D35400] hover:text-[#A04000]">
            <Plus size={14} /> New Client (not on file yet)
          </button>
        )}
      </div>
    </div>
  );
}

export function FindClientOrPropertyDialog({ open, onClose, mode = 'go' }) {
  const navigate = useNavigate();
  const [newClientOpen, setNewClientOpen] = useState(false);
  return (
    <>
    <ClientFormModal open={newClientOpen} onClose={() => setNewClientOpen(false)} title="New Client"
      onSaved={(c) => { setNewClientOpen(false); if (c?._id) navigate(`/clients/${c._id}`); }} />
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid={`find-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle>{mode === 'free' ? 'Free Appraisal — find client or property' : 'New Appraisal — find client or property'}</DialogTitle>
          <DialogDescription>
            {mode === 'free'
              ? 'Pick who this free appraisal is for. Existing records are reused — nothing is duplicated.'
              : "You'll be taken to their Client or Property page to choose the appraisal type or quote type."}
          </DialogDescription>
        </DialogHeader>
        <FindClientOrProperty mode={mode} onDone={onClose} onNewClient={() => { onClose(); setNewClientOpen(true); }} />
      </DialogContent>
    </Dialog>
    </>
  );
}
